// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import getColor from "@insite/mobius/utilities/getColor";
import AccountMessagesCount, {
    AccountMessagesCountPresentationProps,
} from "@store/Library/Components/Toolkit/AccountMessagesCount";
import BellLine from "@store/Library/Components/Toolkit/Icons/Bell/BellLine";
import HeaderLink, { HeaderLinkStyles } from "@store/Library/Components/Toolkit/HeaderLink";
import NotificationsMobilePanel from "@store/Library/Components/Notifications/NotificationsMobilePanel";
import NotificationsPanel from "@store/Library/Components/Notifications/NotificationsPanel";
import useNotifications from "@store/Library/Components/Notifications/useNotifications";
import Popover, { PopoverPresentationProps } from "@store/MobiusOverrides/Popover/Popover";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import { useSession } from "@toolkit/Framework/Store/Context/ContextHooks";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useRef, useState } from "react";
import { css } from "styled-components";
import { headerDesktopSelector } from "@store/Themes/common/mixins/header";

export interface NotificationBellStyles {
    accountMessagesCount?: AccountMessagesCountPresentationProps;
    headerLink?: HeaderLinkStyles;
    popover?: PopoverPresentationProps;
}

export const notificationBellStyles: NotificationBellStyles = {
    accountMessagesCount: {
        extendedStyles: {
            messagesCountText: {
                css: css`
                    top: ${pxToRem(-4)};
                    left: ${pxToRem(16)};

                    ${headerDesktopSelector()} {
                        top: ${pxToRem(-2)};
                        left: ${pxToRem(28)};
                    }
                `,
            },
        },
    },
    popover: {
        // Desktop only — at sm and below the widget renders NotificationsMobilePanel instead, so this
        // popover never mounts there and needs no responsive rules.
        contentBodyProps: {
            css: css`
                display: flex;
                flex-direction: column;
                width: ${pxToRem(450)};
                max-height: calc(100vh - 133px);
                overflow: hidden;
                color: ${getColor("text.main")};
                border: 1px solid ${getColor("common.borderLight")};
                border-radius: ${pxToRem(12)};
                box-shadow: ${x => x.theme.shadows[2]};
                background-color: ${getColor("common.background")};
                margin-top: ${pxToRem(10)};
                padding: 0;
            `,
        },
    },
};

const styles = notificationBellStyles;

const NotificationBell: FC = () => {
    const { messages, unreadCount, isLoading, hasLoaded, setMessagesReadStatus } = useNotifications();
    const { language } = useSession();
    const locale = language?.cultureCode || language?.languageCode;

    // At the sm breakpoint and below, the 450px flyout no longer fits, so notifications open as a full-screen panel instead
    const isMobile = useIsBreakpoint("sm", "down");
    const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);

    // Current page type, appended to action links so the target page can link back to where the
    // notification was opened from.
    const currentPageType = useAppSelector(state => getCurrentPage(state).type);

    // Treated as being "inside" the popover so clicks within the flyout don't close it.
    const contentBodyRef = useRef<HTMLUListElement>(null);
    const popoverRef = useRef<React.ComponentRef<typeof Popover>>(null);

    // Close the surface when an action link navigates away (client-side routing leaves it open otherwise).
    const closeMobilePanel = () => setIsMobilePanelOpen(false);
    // closePopover expects a real DOM event (it forwards it to onClose); synthesize one since this
    // fires from a Link's onClick, not from Popover's own key/click-outside handling.
    const closeFlyout = () => popoverRef.current?.closePopover(new Event("click"));

    const panelData = {
        messages,
        unreadCount,
        isLoading,
        hasLoaded,
        language: locale,
        actionReturnPageType: currentPageType,
        onSetReadStatus: setMessagesReadStatus,
    };

    // Shared trigger button; onClick is supplied by the Popover on desktop and opens the panel on mobile.
    const renderTrigger = (onClick?: () => void) => (
        <HeaderLink
            data-test-selector="headerNotificationBell"
            extendedStyles={styles.headerLink}
            iconSrc={BellLine}
            label={translate("Notifications")}
            onClick={onClick}
            showIconOnly
        >
            <AccountMessagesCount forceShow={true} {...styles.accountMessagesCount} />
        </HeaderLink>
    );

    if (isMobile) {
        return (
            <>
                {renderTrigger(() => setIsMobilePanelOpen(true))}
                <NotificationsMobilePanel
                    isOpen={isMobilePanelOpen}
                    onClose={closeMobilePanel}
                    onActionClick={closeMobilePanel}
                    {...panelData}
                />
            </>
        );
    }

    return (
        <Popover
            {...styles.popover}
            controlsId="notificationsFlyout"
            ref={popoverRef}
            insideRefs={[contentBodyRef]}
            positionFunction={() => ({ position: "absolute", right: 0 })}
            contentBodyProps={{
                ...styles.popover?.contentBodyProps,
                as: "div",
                ref: contentBodyRef as React.RefObject<HTMLUListElement>,
            }}
            popoverTrigger={renderTrigger()}
        >
            <NotificationsPanel {...panelData} onActionClick={closeFlyout} />
        </Popover>
    );
};

const widgetModule: WidgetModule = {
    component: NotificationBell,
    definition: {
        displayName: "Notifications",
        icon: "bell",
        group: "Common",
        fieldDefinitions: [],
    },
};

export default widgetModule;
